import type { ITheme } from '@ben/orion/utils/theme';
import { VisuallyHidden } from '@mantine/core';
import type { ColumnPinningPosition, HeaderGroup } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { sumBy } from 'lodash';
import { type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { TableSortIcon } from '../table-sort-icon';

interface TableHeadRowProps<T> {
  headerGroup: HeaderGroup<T>;
  hasDragHandle?: boolean;
}

const TableHeadRow = <T,>({
  headerGroup,
  hasDragHandle = false,
}: TableHeadRowProps<T>) => {
  const { t } = useTranslation();
  const totalWidth = sumBy(headerGroup.headers, (header) => {
    const columnFlex = header.column.columnDef.meta?.flex ?? 1;
    return header.column.columnDef.meta?.hug ? 0 : columnFlex;
  });

  return (
    <tr key={headerGroup.id}>
      {hasDragHandle && (
        <StyledTableHead>
          <VisuallyHidden>{t('table.dnd.dragHandle')}</VisuallyHidden>
        </StyledTableHead>
      )}
      {headerGroup.headers.map((header) => {
        const canSort = header.column.getCanSort();
        const isSorted = header.column.getIsSorted();
        const sortType = isSorted === 'desc' ? 'descending' : 'ascending';
        const ariaSort = isSorted ? sortType : 'none';
        const onClick = canSort
          ? header.column.getToggleSortingHandler()
          : undefined;

        const getColumnWidth = () => {
          if (header.column.columnDef.meta?.hug) {
            return '0px';
          }

          const columnFlex = header.column.columnDef.meta?.flex ?? 1;
          return `${(columnFlex / totalWidth) * 100}%`;
        };

        return (
          <StyledTableHead
            key={header.id}
            role="columnheader"
            onClick={onClick}
            tabIndex={canSort ? 0 : undefined}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                onClick?.(event);
              }
            }}
            isSorted={!!isSorted}
            isPinned={header.column.getIsPinned()}
            textAlign={header.column.columnDef.meta?.align}
            style={{
              width: getColumnWidth(),
              minWidth: header.column.columnDef.meta?.minWidth,
            }}
            aria-sort={canSort ? ariaSort : undefined}
          >
            {flexRender(header.column.columnDef.header, header.getContext())}
            {canSort && <TableSortIcon isSorted={isSorted} />}
          </StyledTableHead>
        );
      })}
    </tr>
  );
};

export default TableHeadRow;

const StyledTableHead = styled.th`
  ${({
    theme,
    onClick,
    isSorted,
    isPinned,
    textAlign,
  }: {
    theme: ITheme;
    onClick?: (event: MouseEvent<HTMLTableCellElement>) => void;
    isSorted?: boolean;
    isPinned?: ColumnPinningPosition;
    textAlign?: string;
  }) => css`
    ${theme.typography.bodyMd2};
    color: ${theme.palette.neutral[90]};
    padding: ${theme.spacing(2)};
    background-color: ${theme.palette.neutral[50]};
    border-bottom: 1px solid ${theme.palette.neutral[60]};
    text-align: ${textAlign ?? 'left'};
    cursor: ${onClick ? 'pointer' : 'default'};
    white-space: nowrap;

    // Pinned column styles
    position: ${isPinned ? 'sticky' : undefined};
    left: ${isPinned === 'left' ? '0' : undefined};
    right: ${isPinned === 'right' ? '0' : undefined};
    background-color: ${isPinned
      ? `${theme.palette.neutral[50]}E6` // ~90% opacity
      : undefined};

    // Hover state for sort icon
    ${!isSorted &&
    `
      &:hover path {
        fill-opacity: 1;
      }
    `}
  `}
`;
